import type { UserInfo } from './Pollworker';
import type { ModuleSecurityLevel } from '../enums';
import type { CustomerInfo } from 'shared/src/types';

export type SecurityLevel = {
  active: boolean;
  customerInfo: CustomerInfo;
  defaultSurveyTaskUser: boolean;
  easyChatLevel: ModuleSecurityLevel;
  easyFileLevel: ModuleSecurityLevel;
  easyFocusLevel: ModuleSecurityLevel;
  easyFocusMobileLevel: ModuleSecurityLevel;
  easyInventoryLevel: ModuleSecurityLevel;
  easyInventoryMobileLevel: ModuleSecurityLevel;
  easyPollbookAbsenteeLevel: ModuleSecurityLevel;
  easyPollbookLevel: ModuleSecurityLevel;
  easyPollbookProvisionalLevel: ModuleSecurityLevel;
  easyPollworkerLevel: ModuleSecurityLevel;
  easyPollworkerMobileLevel: ModuleSecurityLevel;
  easyShareLevel: ModuleSecurityLevel;
  isDeploymentDriver: boolean;
  isHardwareTechnician: boolean;
  keyCustomerId: string;
  securityLevelName: string;
  systemAdmin: boolean;
  createdAt: string;
  deleted: boolean;
  id: string;
  updatedAt: string;
};

export type SecurityLevelPermission = {
  id: string;
  keyCustomerId: string;
  keySecurityLevelId: string;
  module: number;
  permissionType: number;
  permissionLevel: ModuleSecurityLevel;
  version: string;
  createdAt: string;
  updatedAt: string;
  deleted: boolean;
};

export type SecurityLevelUser = {
  deleted: boolean;
  hourlyRate: number;
  id: string;
  keyEVUserId: string;
  keySecurityLevelId: string;
  presenceStatus: boolean;
  userInfo: UserInfo;
};

export enum PermissionKey {
  EasyChatLevel = 'easyChatLevel',
  EasyFileLevel = 'easyFileLevel', // EasyCampaignFinance
  EasyFocusLevel = 'easyFocusLevel',
  EasyFocusMobileLevel = 'easyFocusMobileLevel',
  EasyInventoryLevel = 'easyInventoryLevel',
  EasyInventoryMobileLevel = 'easyInventoryMobileLevel',
  EasyPollbookAbsenteeLevel = 'easyPollbookAbsenteeLevel',
  EasyPollbookLevel = 'easyPollbookLevel',
  EasyPollbookProvisionalLevel = 'easyPollbookProvisionalLevel',
  EasyPollworkerLevel = 'easyPollworkerLevel',
  EasyPollworkerMobileLevel = 'easyPollworkerMobileLevel',
  EasyShareLevel = 'easyShareLevel',
}

export const PermissionKeyToAppName: Record<PermissionKey, string> = {
  [PermissionKey.EasyChatLevel]: 'EasyChat',
  [PermissionKey.EasyFileLevel]: 'EasyCampaignFinance',
  [PermissionKey.EasyFocusLevel]: 'EasyFocus',
  [PermissionKey.EasyFocusMobileLevel]: 'EasyFocusMobile',
  [PermissionKey.EasyInventoryLevel]: 'EasyInventory',
  [PermissionKey.EasyInventoryMobileLevel]: 'EasyInventoryMobile',
  [PermissionKey.EasyPollbookAbsenteeLevel]: 'EasyPollbookAbsentee',
  [PermissionKey.EasyPollbookLevel]: 'EasyPollbook',
  [PermissionKey.EasyPollbookProvisionalLevel]: 'EasyPollbookProvisional',
  [PermissionKey.EasyPollworkerLevel]: 'EasyPollworker',
  [PermissionKey.EasyPollworkerMobileLevel]: 'EasyPollworkerMobile',
  [PermissionKey.EasyShareLevel]: 'EasyShare',
};

export type AppPermissionKey =
  | PermissionKey.EasyChatLevel
  | PermissionKey.EasyFileLevel
  | PermissionKey.EasyFocusLevel
  | PermissionKey.EasyInventoryLevel
  | PermissionKey.EasyPollbookAbsenteeLevel
  | PermissionKey.EasyPollworkerLevel
  | PermissionKey.EasyShareLevel;

const permissionReaders = {
  [PermissionKey.EasyChatLevel]: (a: CustomerInfo) => a.easyChatClient,
  [PermissionKey.EasyFileLevel]: (a: CustomerInfo) => a.easyFileClient,
  [PermissionKey.EasyFocusLevel]: (a: CustomerInfo) => a.easyFocusClient,
  [PermissionKey.EasyInventoryLevel]: (a: CustomerInfo) => a.easyInventoryClient,
  [PermissionKey.EasyPollbookAbsenteeLevel]: (a: CustomerInfo) => a.easyAbsentee,
  [PermissionKey.EasyPollworkerLevel]: (a: CustomerInfo) => a.easyPollWorkerClient,
  [PermissionKey.EasyShareLevel]: (a: CustomerInfo) => a.easyShareClient,
} satisfies Record<AppPermissionKey, (a: CustomerInfo) => boolean>;

export function resolvePermissionKeyToSubscription(key: AppPermissionKey, account: CustomerInfo): boolean {
  return permissionReaders[key](account) || false;
}
